import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Mail, MapPin, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { COMPANY, API_URL } from "@/config/company";
import { VALUES } from "@/data/site";

export default function Careers() {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/jobs`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setJobs)
      .catch(() => setJobs([]));
  }, []);
  return (
    <>
      <SEO
        title="Careers"
        description="Careers at Numeric Consulting & Solutions — a consulting practice built on integrity, practicality and continual improvement. See current openings or express interest in future roles."
        path="/careers"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }]}
      />
      <PageHero
        eyebrow="Careers"
        title="Build Your Career Where Standards Meet Practice"
        description="We are a consulting practice that takes its own advice: disciplined work, honest communication and continual improvement — for clients and for our own people."
        breadcrumb="Careers"
      />

      {/* Current openings */}
      <section className="bg-white py-16 sm:py-24" data-testid="careers-openings">
        <div className="container-site max-w-3xl">
          <Reveal>
            <p className="eyebrow mb-4">Current Openings</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">Open Positions</h2>
          </Reveal>
          {jobs !== null && jobs.length > 0 ? (
            <div className="mt-8 space-y-5" data-testid="careers-jobs-list">
              {jobs.map((job) => (
                <Reveal key={job.id}>
                  <article className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-8 hover:border-corp/25 hover:shadow-md transition-[box-shadow,border-color] duration-300" data-testid={`job-card-${job.id}`}>
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-mist border border-slate-200 px-3 py-1 text-xs font-medium text-ink">
                        <MapPin size={12} className="text-corp" aria-hidden="true" /> {job.location}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-mist border border-slate-200 px-3 py-1 text-xs font-medium text-ink">
                        <Clock size={12} className="text-corp" aria-hidden="true" /> {job.job_type}
                      </span>
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-semibold text-navy">{job.title}</h3>
                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted whitespace-pre-wrap">{job.description}</p>
                    <a
                      href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(`Application: ${job.title}`)}`}
                      data-testid={`job-apply-${job.id}`}
                      className="mt-5 inline-flex items-center gap-2 bg-corp hover:bg-bright text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors"
                    >
                      <Mail size={14} aria-hidden="true" /> Apply by Email
                    </a>
                  </article>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal delay={0.1}>
              <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-mist p-10 sm:p-12 text-center" data-testid="careers-empty-state">
                <div className="mx-auto w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-teal">
                  <Briefcase size={24} aria-hidden="true" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-navy">No open positions right now</h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted max-w-lg mx-auto">
                  We do not have any advertised vacancies at the moment. New roles in consulting, security
                  assessment and training are posted here as they become available.
                </p>
              </div>
            </Reveal>
          )}
          <Reveal delay={0.15}>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 sm:p-10" data-testid="careers-speculative">
              <h3 className="font-heading text-lg font-semibold text-navy">Express interest in future roles</h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted">
                If your background is in management systems, information security, cybersecurity, business
                continuity or technology consulting, we are glad to hear from you speculatively. Send your
                CV and a short note about the work you do best to{" "}
                <a href={`mailto:${COMPANY.email}`} data-testid="careers-email-link" className="font-semibold text-corp hover:text-bright transition-colors">
                  {COMPANY.email}
                </a>
                , or reach us through the contact form. We keep genuine expressions of interest on file and
                respond when a suitable role opens.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={`mailto:${COMPANY.email}?subject=Expression%20of%20Interest%20—%20Future%20Roles`}
                  data-testid="careers-send-cv-btn"
                  className="inline-flex items-center justify-center gap-2 bg-corp hover:bg-bright text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors"
                >
                  <Mail size={15} aria-hidden="true" /> Email Your CV
                </a>
                <Link
                  to="/contact"
                  data-testid="careers-contact-btn"
                  className="inline-flex items-center justify-center gap-2 border border-corp text-corp hover:bg-corp hover:text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors"
                >
                  Use the Contact Form <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How we work */}
      <section className="bg-mist py-16 sm:py-24" data-testid="careers-culture">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <p className="eyebrow mb-4">How We Work</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              The Values We Hire For
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted">
              Our team brings more than {COMPANY.experienceYears} years of collective experience. What holds
              that experience together is a shared way of working.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.name} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-7">
                  <span className="font-mono text-xs font-semibold text-teal-dark" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-ink">{v.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{v.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Want to Know More About Working With Us?"
        description="Whether as a future team member or as a client, the conversation starts the same way — get in touch."
      />
    </>
  );
}
